/**
 * DataRegistry — host-side data source aggregator.
 *
 * This file is intentionally thin: it imports domain-owned resolver maps and
 * merges them into the single DATA_REGISTRY lookup used by the dashboard host.
 * All query logic, response types, and normalizers live in the domain files
 * under `../data-sources/`.
 *
 * Backward-compatibility surface (unchanged import paths):
 *   import { DataResolver }              from "@/lib/dashboard/DataRegistry"
 *   import { DATA_REGISTRY }             from "@/lib/dashboard/DataRegistry"
 *   import { fetchCampaignScheduleData } from "@/lib/dashboard/DataRegistry"
 */

// ── Domain resolver maps ──────────────────────────────────────────────────────

import { detectionDataSources } from "@/lib/live/data-sources/detection";
import { enginesDataSources } from "@/lib/live/data-sources/engines";
import {
  eventsDataSources,
  fetchCampaignScheduleData as _fetchCampaignScheduleData,
} from "@/lib/live/data-sources/events";
import { anomaliesDataSources } from "@/lib/live/data-sources/anomalies";
import { infringementDataSources } from "@/lib/live/data-sources/infringement";
import { kpiDataSources } from "@/lib/live/data-sources/kpi";
import { reportDataSources } from "@/lib/live/data-sources/reports";
import { cdnsaDataSources } from "@/lib/live/data-sources/cdnsa";
import { reportSnapshotDataSources } from "@/lib/live/data-sources/reportSnapshots";

// ── Public API ────────────────────────────────────────────────────────────────

// Re-export the DataResolver type — consumers import it from here.
export type { DataResolver } from "./resolverTypes";

/**
 * Unified registry of all data sources, keyed by source name.
 *
 * The dashboard host resolves a widget's `dataRequest.sourceName` against this
 * map at runtime. Each domain slice owns its keys; merging happens here so the
 * host never touches individual domain files.
 */
const COMPLETE_SOURCES: Readonly<Record<string, string>> = {
  "detection-volume": "All scoped buckets support chart zoom and tooltips.",
  "detection-per-domain": "Domain comparisons require all counts and trends.",
  "pirate-source-distribution": "Category shares require the full distribution denominator.",
  "detection-per-engine": "The scoped engine table displays counts, expectations and trends.",
  "response-per-engine": "The scoped response table compares all engines.",
  "engines-health": "The fixed health table displays every scoped engine status.",
  "system-anomalies": "A bounded cursor page retains anomaly detail and status context.",
  "infringement-records": "A bounded cursor page retains record detail and drill-down context.",
  "event-timeline": "The timeline is a scope selector and needs all events in its window.",
  "event-timeline-advanced": "The monitoring timeline needs all scoped event/status context.",
  "event-evolution":
    "Configured breakdown selection already gates the provider; remaining series support toggles and tooltips.",
  "event-scope": "The bounded event selector needs every event identity in scope.",
  "campaign-schedule":
    "The matrix/list needs all schedule memberships and campaign drill-down context.",
  "safeip-summary": "The fixed safety ratio retains all counts and denominator evidence.",
  "safe-domain-summary":
    "The fixed safety ratio retains all domain counts and denominator evidence.",
  "ip-blocking-cohorts": "The cohort chart needs totals, daily gaps and tenant coverage.",
  "domain-blocking-cohorts":
    "The domain cohort chart needs totals, daily gaps and tenant coverage.",
  "ip-blocking-effectiveness":
    "Trend, summary and operator rankings share qualified sending aggregates.",
  "domain-blocking-effectiveness":
    "Domain trend, summary and rankings share qualified sending aggregates.",
  "ip-blocking-combined-hosts": "All scoped host rows determine the ranked share denominator.",
  "domain-blocking-combined-hosts":
    "All scoped domain host rows determine the ranked share denominator.",
  "ip-blocking": "The fixed daily detected/sent chart retains daily counts, gaps and freshness.",
  "ip-blocking-top-hosts": "Channel totals and attribution are required for ranked shares.",
  "network-id-weekly":
    "Every week and group determines weekly shares, the top two and weekly maxima.",
};
const UNAVAILABLE_SOURCES = new Set([
  "network-id",
  "ott-piracy-metrics",
  "ott-piracy-terms",
  "ott-piracy-chain",
]);

const sources: Record<string, import("./resolverTypes").DataResolver> = {
  ...detectionDataSources,
  ...enginesDataSources,
  ...eventsDataSources,
  ...anomaliesDataSources,
  ...infringementDataSources,
  ...kpiDataSources,
  ...reportDataSources,
  ...cdnsaDataSources,
  ...reportSnapshotDataSources,
};

export const DATA_REGISTRY: Record<string, import("./resolverTypes").DataResolver> =
  Object.fromEntries(
    Object.entries(sources).map(([source, resolver]) => [
      source,
      {
        ...resolver,
        selection:
          resolver.selection ??
          (UNAVAILABLE_SOURCES.has(source)
            ? {
                mode: "unavailable" as const,
                reason: "This source has no persisted-data provider.",
              }
            : COMPLETE_SOURCES[source]
              ? { mode: "complete" as const, reason: COMPLETE_SOURCES[source] }
              : undefined),
      },
    ]),
  );

/**
 * Direct campaign-schedule fetch, preserved at this path for backward
 * compatibility with hooks and composition blocks that bypass the widget system.
 *
 * @example
 *   import { fetchCampaignScheduleData } from "@/lib/dashboard/DataRegistry";
 *   const data = await fetchCampaignScheduleData(client, tenantIds);
 */
export const fetchCampaignScheduleData = _fetchCampaignScheduleData;
